import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Brain, ArrowLeft, RefreshCw, Rocket, Radar, LineChart, Trash2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const TABS = [
  { key: "overview",    label: "Overview",       icon: Brain },
  { key: "content",     label: "Content Engine", icon: Rocket },
  { key: "visibility",  label: "AI Visibility",  icon: Radar },
  { key: "gsc",         label: "Search Console", icon: LineChart },
];

const BUCKET_LABELS = {
  howto: "How-to",
  gift: "Gift ideas",
  compare: "Comparisons",
  realreview: "Real reviews",
};

export default function BrainControl() {
  const [tab, setTab] = useState("overview");
  const [status, setStatus] = useState(null);
  const [buckets, setBuckets] = useState(null);
  const [opps, setOpps] = useState([]);
  const [citations, setCitations] = useState(null);
  const [gsc, setGsc] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const [s, b, o] = await Promise.all([
        api.get("/rex/status", { withCredentials: true }),
        api.get("/rex/buckets", { withCredentials: true }),
        api.get("/rex/opportunities", { withCredentials: true, params: { status: "pending" } }),
      ]);
      setStatus(s.data); setBuckets(b.data); setOpps(o.data.opportunities || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Load failed");
    }
  };
  const loadCitations = async () => {
    try {
      const r = await api.get("/rex/citations", { withCredentials: true, params: { limit: 100 } });
      setCitations(r.data);
    } catch (e) { setError(e?.response?.data?.detail || "Citations load failed"); }
  };
  const loadGsc = async () => {
    try {
      const r = await api.get("/rex/search-console", { withCredentials: true });
      setGsc(r.data);
    } catch (e) { setError(e?.response?.data?.detail || "GSC load failed"); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "visibility" && !citations) loadCitations();
    if (tab === "gsc" && !gsc) loadGsc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const discover = async () => {
    setBusy("discover");
    try { await api.post("/rex/discover", {}, { withCredentials: true }); await load(); }
    catch (e) { setError(e?.response?.data?.detail || "Discovery failed"); }
    finally { setBusy(null); }
  };
  const gscSync = async () => {
    setBusy("gsc");
    try { await api.post("/rex/gsc-sync", {}, { withCredentials: true }); await loadGsc(); }
    catch (e) { setError(e?.response?.data?.detail || "GSC sync failed — provide creds in .env first"); }
    finally { setBusy(null); }
  };
  const removeOpp = async (id) => {
    if (!window.confirm("Remove this opportunity?")) return;
    await api.delete(`/rex/opportunity/${id}`, { withCredentials: true });
    await load();
  };

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12" data-testid="brain-control-page">
      <Link to="/admin" className="text-white/50 hover:text-white text-xs uppercase tracking-widest inline-flex items-center gap-2 mb-6"
        data-testid="brain-back-link">
        <ArrowLeft size={14} /> Control Center
      </Link>

      <div className="flex items-center gap-4 mb-3">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF3D8B] to-[#A855F7] flex items-center justify-center" data-testid="brain-logo">
          <Brain size={26} className="text-black" />
        </div>
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-white">B.R.A.I.N.</h1>
          <div className="text-[#A855F7] text-xs uppercase tracking-widest">Booty & Breezy Ranking Adaptive Intelligence Network</div>
        </div>
      </div>

      <p className="text-white/60 max-w-2xl text-sm mb-8">
        The growth engine. Discovers content gaps, scores them, and hands winners to the content team to publish.
      </p>

      {error && (
        <div className="mb-6 p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid="brain-error">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-white/10 mb-8">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`brain-tab-${key}`}
            className={`px-4 py-2.5 text-xs uppercase tracking-widest transition-colors inline-flex items-center gap-2 ${
              tab === key ? "text-[#FF3D8B] border-b-2 border-[#FF3D8B]" : "text-white/50 hover:text-white"
            }`}
          >
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {tab === "overview"   && <Overview status={status} buckets={buckets} onDiscover={discover} busy={busy} onReload={load} />}
      {tab === "content"    && <ContentEngine buckets={buckets} opps={opps} onRemove={removeOpp} />}
      {tab === "visibility" && <AiVisibility citations={citations} status={status} onReload={loadCitations} />}
      {tab === "gsc"        && <SearchConsole gsc={gsc} status={status} onSync={gscSync} busy={busy} />}
    </div>
  );
}

// ---------- Sub-tabs ----------
function Overview({ status, buckets, onDiscover, busy, onReload }) {
  if (!status) return <div className="text-white/50 text-sm">Loading…</div>;
  return (
    <div data-testid="brain-tab-overview-body">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <StatTile testId="brain-stat-opportunities" label="Opportunities" value={status.opportunities_pending} sub={`${status.opportunities_published} published`} />
        <StatTile testId="brain-stat-blogs" label="Blog posts" value={status.blog_posts_published} sub="live on site" />
        <StatTile testId="brain-stat-programmatic" label="Programmatic" value={status.programmatic_pages} sub="pages" />
        <StatTile testId="brain-stat-reviews" label="Real reviews" value={status.reviews_published} sub="social proof" />
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        <StatusPill label="Perplexity" ok={status.perplexity_ready} />
        <StatusPill label="GSC" ok={status.gsc_ready} />
        <StatusPill label="Email" ok={status.email_ready} />
        <StatusPill label={`Aggressiveness ${status.aggressiveness}`} ok />
      </div>

      <div className="flex flex-wrap gap-3 mb-10">
        <button onClick={onDiscover} disabled={busy === "discover"} data-testid="brain-discover-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] disabled:opacity-50">
          {busy === "discover" ? "Thinking…" : "Run Discovery"}
        </button>
        <button onClick={onReload} data-testid="brain-reload-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white/70 hover:border-white/40 uppercase tracking-widest text-xs">
          <RefreshCw size={13} /> Reload
        </button>
      </div>

      {buckets && (
        <>
          <div className="text-white/60 text-xs uppercase tracking-widest mb-3">Bucket competition</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {buckets.buckets.map((b) => (
              <div key={b.bucket} className="rounded-xl border border-white/10 bg-[#0f0f0f] p-4" data-testid={`brain-bucket-${b.bucket}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/80 text-sm font-semibold">{BUCKET_LABELS[b.bucket] || b.bucket}</span>
                  <span className="text-[#FF3D8B] text-xs font-mono">{(b.weight * 100).toFixed(0)}%</span>
                </div>
                <div className="text-white/50 text-xs">Ready {b.ready}/{b.target} · Published {b.published}</div>
                <div className="text-white/40 text-[10px] mt-1">GSC clicks {b.gsc_clicks} · impressions {b.gsc_impressions}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ContentEngine({ buckets, opps, onRemove }) {
  return (
    <div data-testid="brain-tab-content-body">
      <div className="text-white/60 text-xs uppercase tracking-widest mb-3">
        Pending opportunities · {opps.length}
      </div>
      <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden">
        {opps.length === 0 ? (
          <div className="p-8 text-center text-white/40 text-sm">
            No opportunities queued. Run discovery from the Overview tab.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-white/50 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Topic</th>
                <th className="text-left p-3">Bucket</th>
                <th className="text-left p-3">Score</th>
                <th className="text-left p-3">Rationale</th>
                <th className="text-left p-3"></th>
              </tr>
            </thead>
            <tbody>
              {opps.map((o) => (
                <tr key={o.id} className="border-t border-white/5" data-testid={`brain-opp-${o.id}`}>
                  <td className="p-3 text-white">{o.topic}</td>
                  <td className="p-3"><span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#A855F7]/20 text-[#A855F7]">
                    {BUCKET_LABELS[o.bucket] || o.bucket}</span>
                  </td>
                  <td className="p-3 text-[#FF3D8B] font-mono">{o.score}</td>
                  <td className="p-3 text-white/60 text-xs">{o.rationale || "—"}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => onRemove(o.id)} data-testid={`brain-opp-del-${o.id}`}
                      className="text-white/40 hover:text-red-400" aria-label="Remove opportunity">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AiVisibility({ citations, status, onReload }) {
  return (
    <div data-testid="brain-tab-visibility-body">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-white/60 text-xs uppercase tracking-widest">Share of AI voice</div>
          <div className="font-display text-4xl text-white mt-1">
            {citations ? `${(citations.share_of_voice * 100).toFixed(1)}%` : "—"}
          </div>
          <div className="text-white/40 text-xs mt-1">
            {citations?.active_questions ?? 0} probe questions active
          </div>
        </div>
        <button onClick={onReload} data-testid="brain-visibility-reload"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white/70 uppercase tracking-widest text-xs">
          <RefreshCw size={13} /> Reload
        </button>
      </div>

      {!(status?.perplexity_ready) && (
        <div className="mb-4 p-4 rounded-lg border border-yellow-500/40 bg-yellow-500/10 text-yellow-200 text-sm">
          Perplexity API key not set. Add <span className="font-mono">PERPLEXITY_API_KEY</span> to <span className="font-mono">/app/backend/.env</span> to run AI citation probes.
        </div>
      )}

      <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden">
        {!citations || citations.citations.length === 0 ? (
          <div className="p-8 text-center text-white/40 text-sm">
            No citation probes have run yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-white/50 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Engine</th>
                <th className="text-left p-3">Question</th>
                <th className="text-left p-3">Mentioned</th>
                <th className="text-left p-3">When</th>
              </tr>
            </thead>
            <tbody>
              {citations.citations.slice(0, 30).map((c, i) => (
                <tr key={`${c.question_id || i}-${c.checked_at}`} className="border-t border-white/5">
                  <td className="p-3 text-white/80 capitalize">{c.engine || "—"}</td>
                  <td className="p-3 text-white/70 text-xs max-w-md truncate">{c.question_text || c.question || "—"}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      c.mentioned ? "bg-emerald-500/20 text-emerald-400" : "bg-white/5 text-white/40"
                    }`}>{c.mentioned ? "Yes" : "No"}</span>
                  </td>
                  <td className="p-3 text-white/40 text-xs">{(c.checked_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SearchConsole({ gsc, status, onSync, busy }) {
  return (
    <div data-testid="brain-tab-gsc-body">
      {!(status?.gsc_ready) && (
        <div className="mb-4 p-4 rounded-lg border border-yellow-500/40 bg-yellow-500/10 text-yellow-200 text-sm">
          Google Search Console not connected. Add <span className="font-mono">GOOGLE_GSC_SA_JSON</span> +
          <span className="font-mono"> GSC_PROPERTY</span> to <span className="font-mono">/app/backend/.env</span>, then hit Sync.
        </div>
      )}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SmallStat label="Impressions" value={gsc?.summary?.impressions ?? 0} />
          <SmallStat label="Clicks" value={gsc?.summary?.clicks ?? 0} />
          <SmallStat label="Ranked queries" value={gsc?.summary?.queries_ranked ?? 0} />
          <SmallStat label="Avg position" value={gsc?.summary?.avg_position ?? "—"} />
        </div>
        <button onClick={onSync} disabled={busy === "gsc"} data-testid="brain-gsc-sync-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] disabled:opacity-50">
          {busy === "gsc" ? "Syncing…" : "Sync GSC"}
        </button>
      </div>

      {gsc?.summary?.property && (
        <div className="mb-4 text-white/50 text-xs">
          Property: <span className="text-white/80">{gsc.summary.property}</span>
          {" "}
          <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer"
            className="text-[#A855F7] inline-flex items-center gap-1 ml-2">
            open <ExternalLink size={11} />
          </a>
        </div>
      )}

      <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden">
        {!gsc || (gsc.queries || []).length === 0 ? (
          <div className="p-8 text-center text-white/40 text-sm">
            No query data yet. Sync once GSC creds are configured.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-white/50 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Query</th>
                <th className="text-left p-3">Impressions</th>
                <th className="text-left p-3">Clicks</th>
                <th className="text-left p-3">Position</th>
              </tr>
            </thead>
            <tbody>
              {gsc.queries.slice(0, 50).map((q, i) => (
                <tr key={`${q.query}-${i}`} className="border-t border-white/5">
                  <td className="p-3 text-white/80">{q.query}</td>
                  <td className="p-3 text-white">{q.impressions}</td>
                  <td className="p-3 text-white">{q.clicks}</td>
                  <td className="p-3 text-white/60">{q.position ? Number(q.position).toFixed(1) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------- Primitives ----------
function StatTile({ label, value, sub, testId }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-4" data-testid={testId}>
      <div className="text-white/50 text-[10px] uppercase tracking-widest mb-1">{label}</div>
      <div className="font-display text-3xl text-white leading-none">{value}</div>
      {sub && <div className="text-white/40 text-xs mt-1.5">{sub}</div>}
    </div>
  );
}

function SmallStat({ label, value }) {
  return (
    <div>
      <div className="text-white/40 text-[10px] uppercase tracking-widest mb-0.5">{label}</div>
      <div className="text-white text-lg font-semibold">{value}</div>
    </div>
  );
}

function StatusPill({ label, ok }) {
  return (
    <span className={`text-[11px] px-3 py-1 rounded-full border ${
      ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-white/10 bg-white/5 text-white/40"
    }`}>
      {ok ? "●" : "○"} {label}
    </span>
  );
}
